/**
 * Beacon How To — in-app process guide for the real product loops.
 * Readable prose (not a card dashboard); links into shell routes.
 */
import { Link } from 'react-router-dom'

const linkClass =
  'font-medium text-teal-700 underline underline-offset-2 hover:text-teal-800'

export default function BeaconHowTo() {
  return (
    <div className="mx-auto max-w-3xl space-y-10">
      <header className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-beacon-orange">
          How To
        </p>
        <h2 className="font-display text-3xl text-slate-900">Using Beacon</h2>
        <p className="max-w-2xl text-sm leading-relaxed text-slate-600">
          Beacon is your personal AWS engineering partner: ask in chat, review what it drafts,
          approve risky moves, and keep a ledger of what it created — with dry-run on by default.
        </p>
      </header>

      {/* Prerequisite: owner/admin links AWS once; members never enter keys in Settings */}
      <section className="space-y-3">
        <h3 className="font-display text-xl text-slate-900">Before you start — AWS connectivity</h3>
        <p className="text-sm leading-relaxed text-slate-700">
          An <strong className="font-semibold text-slate-900">Owner or Admin</strong> links your
          company&apos;s AWS account once under{' '}
          <Link to="/beacon/admin" className={linkClass}>Company Admin</Link>
          {' '}(<code className="rounded bg-slate-100 px-1 py-0.5 text-xs">/beacon/admin</code>).
          Ordinary members do <strong className="font-semibold text-slate-900">not</strong> enter
          AWS credentials or connectivity in{' '}
          <Link to="/settings" className={linkClass}>Settings</Link>.
          After the tenant link is in place, everyone uses{' '}
          <Link to="/" className={linkClass}>Chat</Link> and the shared workspace against that
          company AWS connection.
        </p>
        <p className="text-sm leading-relaxed text-slate-600">
          Settings is for model and environment preferences only — not company AWS connectivity.
          If Chat cannot reach AWS, ask an Owner/Admin to finish linking under Company Admin.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="font-display text-xl text-slate-900">What Beacon is</h3>
        <p className="text-sm leading-relaxed text-slate-700">
          Beacon is a Muse-like shell over a governed AWS agent. It discovers your account
          (inventory, VPC, cost signals), answers in natural language, and can act on an
          allowlisted set of writes — but only after you review plans and sign off on risky
          actions. It is not a full AWS Console replacement; it is a safe operator for day-one
          discover → decide → act → remember → notify.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="font-display text-xl text-slate-900">Chat-first loop</h3>
        <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed text-slate-700">
          <li>
            <strong className="font-semibold text-slate-900">Ask</strong> in{' '}
            <Link to="/" className={linkClass}>Chat</Link> — for example “what’s in my account?”,
            “harden this bucket”, or “design a data pipeline”.
          </li>
          <li>
            When you ask Beacon to design or build, the agent{' '}
            <strong className="font-semibold text-slate-900">drafts an architecture</strong> from
            the conversation (no separate draft form on the Architectures page).
          </li>
          <li>
            Open{' '}
            <Link to="/beacon/architectures" className={linkClass}>Architectures</Link> to{' '}
            <strong className="font-semibold text-slate-900">review</strong> the document, then{' '}
            <strong className="font-semibold text-slate-900">Approve</strong>.
          </li>
          <li>
            Click <strong className="font-semibold text-slate-900">Implement (dry-run)</strong> —
            live apply stays gated; dry-run is the default path so you see the plan before any
            real change.
          </li>
        </ol>
        <p className="text-sm leading-relaxed text-slate-600">
          Keep the dry-run checkbox on in Chat unless you intentionally want live tool execution
          for allowlisted, low-risk actions.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="font-display text-xl text-slate-900">Approvals (HITL inbox)</h3>
        <p className="text-sm leading-relaxed text-slate-700">
          <Link to="/beacon/approvals" className={linkClass}>Approvals</Link> is the human-in-the-loop
          inbox for <em>any</em> risky action — not only architectures. Deletes, public exposure,
          broad security-group changes, and similar high-risk writes land here as pending cards.
          Approve or reject each item; Owners/Admins decide, Team Members may request writes depending
          on tenant permissions.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="font-display text-xl text-slate-900">Resources ledger</h3>
        <p className="text-sm leading-relaxed text-slate-700">
          <Link to="/beacon/resources" className={linkClass}>Resources</Link> is the time-windowed
          ledger of what Beacon created or tracks (S3, security groups, schedules, and so on).
          Filter by day / week / month to answer “what did you create this week?” Deleting an
          active row often queues HITL instead of deleting immediately.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="font-display text-xl text-slate-900">Tasks, scheduling, monitor + email</h3>
        <p className="text-sm leading-relaxed text-slate-700">
          Use <Link to="/beacon/tasks" className={linkClass}>Tasks</Link> for one-off and recurring
          jobs Beacon owns (for example a daily open-SG check). Schedule from Chat or the Tasks
          UI, then run due work from the page. Monitor findings also surface under{' '}
          <Link to="/beacon/recommendations" className={linkClass}>Recommendations</Link>; email
          alerts go out via Beacon’s SES when a verified From identity is configured (otherwise
          notify stubs gracefully in sandbox).
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="font-display text-xl text-slate-900">VPC and Recommendations</h3>
        <p className="text-sm leading-relaxed text-slate-700">
          <Link to="/beacon/vpc" className={linkClass}>VPC</Link> shows a live snapshot of VPCs,
          subnets, and related networking — refresh from AWS when you want a fresh pull.{' '}
          <Link to="/beacon/recommendations" className={linkClass}>Recommendations</Link> collects
          advisor-style findings and monitor results in a time window (Advise / Run monitor).
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="font-display text-xl text-slate-900">Company admin</h3>
        <p className="text-sm leading-relaxed text-slate-700">
          <Link to="/beacon/admin" className={linkClass}>Company</Link> is the tenant admin surface:
          invite teammates by email (seat limits follow Marketplace tier), manage roles
          (Owner / Admin / Team Member), revoke access, and — for Owners/Admins only — link one
          or more AWS accounts to the company workspace (the single place for Beacon AWS
          connectivity). SSO (OIDC; SAML via IdP bridge) can be enabled for the tenant —
          email/password and local dev tokens still work. Shared ledger, architectures, and
          approvals are scoped to the company, with per-user audit on chat and decisions.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="font-display text-xl text-slate-900">Dry-run vs live / safety</h3>
        <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-slate-700">
          <li>
            <strong className="font-semibold text-slate-900">Dry-run default</strong> — Chat and
            architecture Implement prefer dry-run so Beacon shows intent without mutating AWS.
          </li>
          <li>
            <strong className="font-semibold text-slate-900">Allowlisted writes</strong> — only a
            small set of create/harden/schedule actions run even when live; arbitrary AWS APIs are
            out of scope.
          </li>
          <li>
            <strong className="font-semibold text-slate-900">HITL for risk</strong> — destructive or
            exposure-heavy actions always wait in Approvals.
          </li>
          <li>
            <strong className="font-semibold text-slate-900">Remember</strong> — every create/update/delete
            Beacon owns is written to the Resources ledger for later questions and drift checks.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-slate-600">
          Model and environment preferences live under{' '}
          <Link to="/settings" className={linkClass}>Settings</Link>
          {' '}— not AWS account linking (that stays under{' '}
          <Link to="/beacon/admin" className={linkClass}>Company Admin</Link>).
        </p>
      </section>
    </div>
  )
}
